import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { loginStaff, checkTenantSubscription, persistStaffAuth } from '../../services/api.js';

/* ─────────────────────────────────────────────────────────────────────────────
   Global keyframe + layout styles (injected once)
   ───────────────────────────────────────────────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  * { box-sizing: border-box; }

  @keyframes fadeIn     { from { opacity: 0 }                            to { opacity: 1 } }
  @keyframes slideUp    { from { transform: translateY(20px); opacity:0 } to { transform:translateY(0); opacity:1 } }
  @keyframes slideRight { from { transform: translateX(-24px); opacity:0 } to { transform:translateX(0); opacity:1 } }
  @keyframes shrink     { from { width:100% }                            to { width:0% } }
  @keyframes shimmer    { 0%,100% { opacity:.5 }                         50% { opacity:1 } }
  @keyframes floatUp    { 0%,100% { transform:translateY(0) }            50% { transform:translateY(-6px) } }
  @keyframes pulse      { 0%,100% { transform:scale(1) }                 50% { transform:scale(1.04) } }
  @keyframes spin       { to { transform:rotate(360deg) } }

  /* ── Page structure ─────────────────────────────────── */

  .page-shell {
    height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    font-family: DM Sans, sans-serif;
    background: #F5F6FA;
  }

  .split-container {
    flex: 1;
    display: flex;
    min-height: 0;          /* critical: lets children respect parent height */
    overflow: hidden;
  }

  /* ── Left panel ─────────────────────────────────────── */

  .left-panel {
    width: 40%;
    min-width: 290px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 32px 28px 24px;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    animation: slideRight .45s ease;
  }

  .lp-glow-bg  { position:absolute; inset:0; pointer-events:none; }
  .lp-glow-orb { position:absolute; top:-10%; right:-8%; width:60%; padding-bottom:60%; border-radius:50%; pointer-events:none; }

  .lp-top     { position:relative; z-index:1; }
  .lp-artwork { position:relative; z-index:1; display:flex; justify-content:center; margin:10px 0 6px; }
  .lp-pills   { position:relative; z-index:1; }
  .lp-divider { width:40px; height:2px; border-radius:1px; margin-bottom:20px; }
  .lp-tagline { max-width:260px; margin:0; }

  /* ── Right panel ─────────────────────────────────────── */

  .right-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 28px 36px;
    overflow-y: auto;
    animation: slideUp .4s ease;
  }

  .form-container { width:100%; max-width:390px; }

  .form-heading {
    text-align: center;
    margin-bottom: 22px;
  }

  /* ── Footer ─────────────────────────────────────────── */

  .page-footer {
    flex-shrink: 0;
    padding: 9px 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  /* ── Form controls ─────────────────────────────────── */

  .login-input {
    width: 100%;
    border: 1.5px solid #DDE2EB;
    border-radius: 10px;
    padding: 12px 15px;
    font-size: 14px;
    font-family: DM Sans, sans-serif;
    outline: none;
    background: #FAFBFC;
    transition: border-color .18s, box-shadow .18s, background .18s;
    line-height: 1;
  }
  .login-input:focus {
    border-color: #1A7F8E;
    background: #fff;
    box-shadow: 0 0 0 3px #1A7F8E18;
  }
  .login-input::placeholder { color: #9BA8B4; }

  .login-btn {
    width: 100%;
    border: none;
    border-radius: 12px;
    padding: 14px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    font-family: DM Sans, sans-serif;
    transition: transform .15s, box-shadow .15s, opacity .15s;
    letter-spacing: .3px;
  }
  .login-btn:hover:not(:disabled) { transform: translateY(-1px); }
  .login-btn:active:not(:disabled) { transform: translateY(0); }
  .login-btn:disabled { cursor: not-allowed; }

  .feature-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,.10);
    border: 1px solid rgba(255,255,255,.18);
    border-radius: 999px;
    padding: 5px 11px;
    font-size: 11px;
    font-weight: 500;
    color: rgba(255,255,255,.88);
    backdrop-filter: blur(6px);
    transition: background .2s;
  }
  .feature-pill:hover { background: rgba(255,255,255,.16); }

  .pwd-toggle {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    line-height: 1;
    transition: color .15s;
  }

  /* ── Mobile layout (≤ 768 px) ────────────────────────── */

  @media (max-width: 768px) {
    .split-container { flex-direction: column; }

    /* Left panel becomes a slim horizontal banner */
    .left-panel {
      width: 100% !important;
      min-width: unset !important;
      flex-direction: row !important;
      align-items: center !important;
      justify-content: flex-start !important;
      padding: 10px 16px !important;
      gap: 12px;
    }

    /* Hide decorative elements */
    .lp-artwork,
    .lp-pills,
    .lp-tagline,
    .lp-divider { display: none !important; }

    /* Compact brand inside banner */
    .lp-top {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      gap: 10px;
    }
    .lp-icon {
      width: 34px !important;
      height: 34px !important;
      border-radius: 9px !important;
      font-size: 16px !important;
      margin-bottom: 0 !important;
    }
    .lp-brand-name  { font-size: 16px !important; margin: 0 !important; line-height: 1.2 !important; }
    .lp-brand-sub   { font-size: 9px !important;  margin: 0 !important; }
    .lp-gold-bar    { display: none !important; }

    /* Right panel: scrollable, start from top */
    .right-panel {
      flex: 1;
      padding: 16px 16px 10px !important;
      justify-content: flex-start !important;
      align-items: stretch !important;
      overflow-y: auto;
    }
    .form-container { max-width: 100% !important; }
    .form-heading   { margin-bottom: 14px !important; }
    .fh-icon {
      width: 44px !important;
      height: 44px !important;
      font-size: 22px !important;
      margin-bottom: 10px !important;
    }
    .fh-title { font-size: 22px !important; }

    .page-footer p { font-size: 10px !important; }
    .page-footer   { padding: 7px 12px !important; }
  }

  /* ── Small phones (≤ 480 px) ──────────────────────────── */
  @media (max-width: 480px) {
    .right-panel { padding: 14px 14px 8px !important; }
  }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   Hotel art SVG — embedded illustration for the left panel
   ───────────────────────────────────────────────────────────────────────────── */
function HotelArtwork() {
  return (
    <svg viewBox="0 0 400 310" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: 340, opacity: .92 }}>
      {/* Stars */}
      {[[60,22],[160,12],[280,18],[350,34],[30,68]].map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="1.6" fill="rgba(255,255,255,.55)"
          style={{ animation: `shimmer ${1.8+i*.4}s ease-in-out infinite` }} />
      ))}
      {[[100,46],[240,40],[320,52],[80,88]].map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="1.1" fill="rgba(255,255,255,.35)"
          style={{ animation: `shimmer ${2.2+i*.3}s ease-in-out infinite` }} />
      ))}

      {/* Moon */}
      <circle cx="348" cy="30" r="18" fill="rgba(255,255,255,.08)" />
      <circle cx="348" cy="30" r="12" fill="rgba(255,255,255,.12)" />
      <circle cx="348" cy="30" r="8"  fill="rgba(255,255,255,.18)" />

      {/* Ground */}
      <rect x="0" y="258" width="400" height="52" fill="rgba(13,27,42,.5)" rx="2" />

      {/* Main building */}
      <rect x="100" y="90" width="200" height="168" fill="rgba(26,127,142,.25)"
        stroke="rgba(26,127,142,.6)" strokeWidth="1.5" rx="2" />
      <polygon points="88,90 200,42 312,90" fill="rgba(255,255,255,.14)"
        stroke="rgba(255,255,255,.4)" strokeWidth="1.5" />
      <rect x="185" y="38" width="30" height="11" fill="rgba(201,168,76,.8)" rx="2" />
      <line x1="200" y1="26" x2="200" y2="40" stroke="rgba(255,255,255,.6)" strokeWidth="1.5" />
      <polygon points="200,28 214,33 200,38" fill="#C9A84C" />

      {/* Windows 3×3 */}
      {[128,178,228].map((x,ci) =>
        [108,140,172].map((y,ri) => (
          <g key={`w${ci}${ri}`}>
            <rect x={x} y={y} width="22" height="20" fill="rgba(255,255,255,.18)"
              stroke="rgba(255,255,255,.35)" strokeWidth="1" rx="2" />
            {(ci + ri) % 2 === 0 && (
              <rect x={x+1} y={y+1} width="20" height="18"
                fill="rgba(201,168,76,.22)" rx="1.5"
                style={{ animation: `shimmer ${2+ci*.5+ri*.3}s ease-in-out infinite` }} />
            )}
            <line x1={x+11} y1={y+1} x2={x+11} y2={y+19} stroke="rgba(255,255,255,.25)" strokeWidth=".8" />
            <line x1={x+1}  y1={y+10} x2={x+21} y2={y+10} stroke="rgba(255,255,255,.25)" strokeWidth=".8" />
          </g>
        ))
      )}

      {/* Door */}
      <rect x="184" y="222" width="32" height="36" fill="rgba(255,255,255,.22)"
        stroke="rgba(255,255,255,.5)" strokeWidth="1.2" rx="3" />
      <rect x="186" y="224" width="12" height="34" fill="rgba(255,255,255,.08)" rx="2" />
      <rect x="200" y="224" width="12" height="34" fill="rgba(255,255,255,.08)" rx="2" />
      <circle cx="199" cy="242" r="2.2" fill="rgba(201,168,76,.8)" />

      {/* Side wings */}
      <rect x="40"  y="158" width="60" height="100" fill="rgba(26,127,142,.18)"
        stroke="rgba(26,127,142,.45)" strokeWidth="1.2" rx="1" />
      {[48,70].map(x => [168,196].map(y => (
        <rect key={`lw${x}${y}`} x={x} y={y} width="16" height="14"
          fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.28)" strokeWidth=".8" rx="1" />
      )))}
      <rect x="300" y="158" width="60" height="100" fill="rgba(26,127,142,.18)"
        stroke="rgba(26,127,142,.45)" strokeWidth="1.2" rx="1" />
      {[308,330].map(x => [168,196].map(y => (
        <rect key={`rw${x}${y}`} x={x} y={y} width="16" height="14"
          fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.28)" strokeWidth=".8" rx="1" />
      )))}

      {/* Canopy */}
      <path d="M164 222 Q200 210 236 222" stroke="rgba(255,255,255,.4)" strokeWidth="1.5" fill="rgba(26,127,142,.35)" />

      {/* Trees */}
      {[60,340].map((x, i) => (
        <g key={`tree${i}`} style={{ animation: `floatUp ${3+i}s ease-in-out infinite` }}>
          <ellipse cx={x} cy="244" rx="16" ry="24" fill="rgba(26,127,142,.4)" />
          <ellipse cx={x} cy="238" rx="12" ry="19" fill="rgba(34,160,178,.35)" />
          <rect x={x-3} y="256" width="6" height="10" fill="rgba(201,168,76,.4)" rx="1" />
        </g>
      ))}

      {/* Path */}
      <path d="M176 258 L224 258 L232 286 L168 286 Z" fill="rgba(255,255,255,.06)" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   AlertModal
   ───────────────────────────────────────────────────────────────────────────── */
function AlertModal({ type, title, message, subMessage, onConfirm, onClose, confirmLabel }) {
  const icon = {
    success: { emoji: '✅', bg: T.successPale, border: T.success, color: T.success },
    error:   { emoji: '❌', bg: T.dangerPale,  border: T.danger,  color: T.danger  },
    blocked: { emoji: '🔒', bg: T.warningPale, border: T.warning, color: T.warning },
  }[type] || { emoji: 'ℹ️', bg: T.tealPale, border: T.teal, color: T.teal };

  const handleBackdrop = (e) => { if (type === 'blocked') return; if (e.target === e.currentTarget) onClose?.(); };

  useEffect(() => {
    if (type !== 'success') return;
    const t = setTimeout(() => onConfirm?.(), 2800);
    return () => clearTimeout(t);
  }, [type, onConfirm]);

  return (
    <div onClick={handleBackdrop} style={{
      position:'fixed', inset:0, background:'rgba(10,21,32,.72)', backdropFilter:'blur(6px)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16,
      animation:'fadeIn .18s ease',
    }}>
      <div style={{
        background:T.white, borderRadius:20, padding:'32px 28px', maxWidth:400, width:'100%',
        textAlign:'center', boxShadow:SHADOW.xl, fontFamily:FONTS.body,
        animation:'slideUp .22s cubic-bezier(.34,1.56,.64,1)',
      }}>
        <div style={{
          width:64, height:64, borderRadius:'50%', background:icon.bg,
          border:`2px solid ${icon.border}20`, display:'inline-flex',
          alignItems:'center', justifyContent:'center', fontSize:30, marginBottom:16,
        }}>{icon.emoji}</div>

        <h2 style={{ fontFamily:FONTS.display, fontSize:22, fontWeight:700,
          color:T.navy, marginBottom:6, marginTop:0 }}>{title}</h2>

        <p style={{ fontSize:13, color:T.slate, lineHeight:1.6,
          marginBottom: subMessage ? 4 : 20 }}>{message}</p>

        {subMessage && (
          <p style={{ fontSize:12, color:T.muted, lineHeight:1.5, marginBottom:20 }}>
            {subMessage}
          </p>
        )}

        {type === 'success' && (
          <div style={{ height:3, background:T.border, borderRadius:2, marginBottom:16, overflow:'hidden' }}>
            <div style={{ height:'100%', background:T.success, borderRadius:2,
              animation:'shrink 2.8s linear forwards', width:'100%' }} />
          </div>
        )}

        {type === 'blocked' ? (
          /* Non-dismissible — no buttons. Tell user to contact admin. */
          <div style={{
            background:'#f0fdfa', border:'1.5px solid #99f6e4',
            borderRadius:10, padding:'14px 18px', marginTop:4,
          }}>
            <p style={{ fontSize:13, color:'#0f4c3a', lineHeight:1.75, margin:0 }}>
              Please ask your <strong>administrator</strong> to activate this
              module from the <strong style={{ color:T.teal }}>Control Centre</strong>.
            </p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {onConfirm && (
              <button onClick={onConfirm} className="login-btn" style={{
                background: type === 'success'
                  ? `linear-gradient(135deg,${T.success},#22A060)`
                  : `linear-gradient(135deg,${T.danger},#E05040)`,
                color: T.white,
                boxShadow: `0 5px 16px ${icon.border}40`,
              }}>
                {confirmLabel || (type === 'success' ? '→ Go to Dashboard' : 'OK')}
              </button>
            )}
            {onClose && type !== 'success' && (
              <button onClick={onClose} style={{
                background:'none', border:`1px solid ${T.border}`, borderRadius:10,
                padding:'9px 18px', fontSize:13, color:T.muted,
                cursor:'pointer', fontFamily:FONTS.body,
              }}>← Back to Login</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   StandaloneLogin
   ───────────────────────────────────────────────────────────────────────────── */
export default function StandaloneLogin({ onLogin }) {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [modal,    setModal]    = useState(null);

  const resolveTenantSlug = useCallback(() => {
    const p = new URLSearchParams(window.location.search);
    const fromUrl   = p.get('tenant');
    const fromStore = localStorage.getItem('hotelmis_tenant_slug');
    if (fromUrl) { localStorage.setItem('hotelmis_tenant_slug', fromUrl); return fromUrl; }
    return fromStore || null;
  }, []);

  const tenantSlug = resolveTenantSlug();

  const hotelDisplayName = tenantSlug
    ? tenantSlug.replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase())
    : null;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim())    { setError('Email address is required');  return; }
    if (!password.trim()) { setError('Password is required');       return; }

    const slug = resolveTenantSlug();
    if (!slug) {
      setModal({
        type:'error', title:'Hotel Not Found',
        message:'We could not determine which hotel you belong to.',
        subMessage:'Please open this app using the link provided by your administrator, or contact Orinest support.',
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Verify tenant subscription
      try {
        const check = await checkTenantSubscription(slug);
        const cd = check?.data ?? check;
        if (cd?.valid === false) {
          setModal({
            type:'blocked', title:'Module Not Activated',
            message:`The Reservation Module is not active for "${slug}".`,
            subMessage: cd.reason || 'No active reservation module subscription.',
            redirectUrl: cd.redirectUrl,
          });
          setLoading(false);
          return;
        }
      } catch (_) { /* fail-open */ }

      // Step 2: Authenticate
      const res  = await loginStaff(email.trim(), password, slug);
      const data = res?.data ?? res;

      if (res?.status === 403 || data?.status === 403) {
        setModal({
          type:'blocked', title:'Access Denied',
          message: data?.message || 'Your hotel does not have an active reservation module subscription.',
          subMessage:'Please contact your administrator.',
        });
        return;
      }

      if (!data?.token && !res?.token) {
        const msg = data?.message || res?.message || 'Invalid email or password';
        if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('tenant')) {
          setModal({
            type:'error', title:'Hotel Not Found',
            message:'We could not find a hotel matching your login details.',
            subMessage:'Check your login link or contact your administrator.',
          });
        } else {
          setError(msg);
        }
        return;
      }

      // Step 3: Success
      const token        = data?.token || res?.token;
      const businessName = data?.businessName || data?.propertyName || data?.fullName
                         || slug.replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase());

      persistStaffAuth({ token, tenantSlug: slug, propertyId: data?.propertyId });

      setModal({
        type:'success', title:'Welcome Back! 👋',
        message:`You are verified for ${businessName}.`,
        subMessage:`Signed in as ${data?.fullName || email} · ${(data?.role||'').replace(/_/g,' ')}`,
        data:{ token, tenantSlug: slug, ...data },
      });

    } catch (err) {
      const msg = err.message || 'Login failed. Please try again.';
      if (msg.toLowerCase().includes('subscription') || msg.toLowerCase().includes('module')) {
        setModal({
          type:'blocked', title:'Module Not Active', message:msg,
          subMessage:'Contact your administrator to activate the reservation module.',
        });
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessConfirm = () => { if (modal?.data) onLogin(modal.data); };
  const handleBlockedConfirm = () => {
    if (modal?.redirectUrl) window.location.href = modal.redirectUrl;
    else setModal(null);
  };

  return (
    <>
      <style>{GLOBAL_STYLES}</style>

      {modal && (
        <AlertModal
          type={modal.type} title={modal.title}
          message={modal.message} subMessage={modal.subMessage}
          onConfirm={
            modal.type === 'success' ? handleSuccessConfirm :
            modal.type === 'blocked' ? handleBlockedConfirm :
            () => setModal(null)
          }
          onClose={() => setModal(null)}
          confirmLabel={
            modal.type === 'success' ? '→ Go to Dashboard' :
            modal.type === 'blocked' ? '🛒 Activate Module' :
            'OK'
          }
        />
      )}

      {/* ── Page shell: fixed 100vh, no scroll ─────────────────────────── */}
      <div className="page-shell">

        <div className="split-container">

          {/* ══════════════════════════════════════════════════════════════
              LEFT PANEL — full panel on desktop, slim banner on mobile
              ══════════════════════════════════════════════════════════ */}
          <div
            className="left-panel"
            style={{
              background: `linear-gradient(160deg, ${T.navyDeep} 0%, ${T.navyMid} 45%, #0F2840 75%, ${T.teal}22 100%)`,
            }}
          >
            {/* Ambient glows */}
            <div className="lp-glow-bg" style={{
              background:`radial-gradient(ellipse 70% 55% at 50% 70%, ${T.teal}18 0%, transparent 70%)`,
            }} />
            <div className="lp-glow-orb" style={{ background:`${T.teal}08` }} />

            {/* Top branding block — visible on all sizes */}
            <div className="lp-top">
              {/* Icon */}
              <div
                className="lp-icon"
                style={{
                  width:46, height:46, borderRadius:14,
                  background:`linear-gradient(135deg, ${T.teal}, ${T.tealLight})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:22, marginBottom:12,
                  boxShadow:`0 5px 18px ${T.teal}55`,
                  animation:'pulse 4s ease-in-out infinite',
                  flexShrink:0,
                }}
              >🏨</div>

              {/* Brand text */}
              <div>
                <h1
                  className="lp-brand-name"
                  style={{
                    fontFamily:FONTS.display, fontWeight:700,
                    fontSize:24, color:T.white,
                    margin:'0 0 3px', letterSpacing:'-.3px',
                  }}
                >HotelMIS</h1>
                <p
                  className="lp-brand-sub"
                  style={{
                    fontSize:11, fontWeight:500, letterSpacing:'1.6px',
                    textTransform:'uppercase', color:T.tealLight,
                    margin:0,
                  }}
                >Reservation · Front Desk · Finance</p>
              </div>
            </div>

            {/* Gold divider — hidden on mobile via CSS */}
            <div
              className="lp-divider"
              style={{ background:`linear-gradient(90deg, ${T.gold}, ${T.goldLight})` }}
            />

            {/* Tagline — hidden on mobile via CSS */}
            <div className="lp-tagline" style={{ position:'relative', zIndex:1, marginBottom:8 }}>
              <p style={{
                fontFamily:FONTS.display, fontSize:19, fontWeight:600,
                color:'rgba(255,255,255,.9)', lineHeight:1.45,
                margin:'0 0 6px',
              }}>
                Away from Home,<br />
                <span style={{ color:T.tealLight }}>Yet Feels Like Home</span>
              </p>
              <p style={{
                fontSize:12, color:'rgba(255,255,255,.5)', lineHeight:1.6,
                maxWidth:250, margin:0,
              }}>
                Complete hotel management — reservations, housekeeping, billing, and more.
              </p>
            </div>

            {/* Artwork — hidden on mobile via CSS */}
            <div className="lp-artwork">
              <HotelArtwork />
            </div>

            {/* Feature pills — hidden on mobile via CSS */}
            <div className="lp-pills" style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
              {['🗓 Reservations','🏠 Front Desk','🧹 Housekeeping','📋 Guest Folio'].map(f => (
                <span key={f} className="feature-pill">{f}</span>
              ))}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              RIGHT PANEL — login form
              ══════════════════════════════════════════════════════════ */}
          <div className="right-panel" style={{ background:T.white }}>

            <div className="form-container">

              {/* Heading */}
              <div className="form-heading">
                <div
                  className="fh-icon"
                  style={{
                    width:54, height:54, borderRadius:16,
                    background:T.tealPale,
                    border:`2px solid ${T.teal}25`,
                    display:'inline-flex', alignItems:'center', justifyContent:'center',
                    fontSize:26, marginBottom:12,
                  }}
                >🏨</div>
                <h2
                  className="fh-title"
                  style={{
                    fontFamily:FONTS.display, fontSize:26, fontWeight:700,
                    color:T.navy, margin:'0 0 4px', letterSpacing:'-.3px',
                  }}
                >Staff Sign In</h2>
                <p style={{ fontSize:13, color:T.muted, margin:0 }}>
                  Hotel Management &amp; Reservation Portal
                </p>
              </div>

              {/* Tenant badge */}
              {hotelDisplayName && (
                <div style={{
                  display:'flex', alignItems:'center', gap:10,
                  background:`linear-gradient(135deg, ${T.tealPale}, #EEF7F9)`,
                  border:`1.5px solid ${T.teal}30`, borderRadius:11,
                  padding:'9px 14px', marginBottom:18,
                }}>
                  <div style={{
                    width:30, height:30, borderRadius:8,
                    background:`linear-gradient(135deg, ${T.teal}, ${T.tealLight})`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:14, flexShrink:0,
                  }}>🏨</div>
                  <div>
                    <p style={{ fontSize:9, fontWeight:700, color:T.teal,
                      textTransform:'uppercase', letterSpacing:'.9px', margin:0 }}>
                      Hotel Portal
                    </p>
                    <p style={{ fontSize:13, fontWeight:600, color:T.navy, margin:0 }}>
                      {hotelDisplayName}
                    </p>
                  </div>
                </div>
              )}

              {/* No-tenant warning */}
              {!tenantSlug && (
                <div style={{
                  background:T.warningPale, border:`1.5px solid ${T.warning}30`,
                  borderRadius:10, padding:'10px 14px', marginBottom:18,
                }}>
                  <p style={{ fontSize:13, color:T.warning, margin:0, fontWeight:500 }}>
                    ⚠️ No hotel detected. Open this app using the link provided by your administrator.
                  </p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:14 }}>

                {/* Email */}
                <div>
                  <label style={{
                    display:'block', fontSize:10, fontWeight:700, color:T.muted,
                    letterSpacing:'.9px', textTransform:'uppercase', marginBottom:6,
                  }}>Email Address</label>
                  <input
                    className="login-input"
                    type="email"
                    placeholder="you@yourhotel.com"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    autoComplete="email"
                    style={{ color:T.navy }}
                    required
                  />
                </div>

                {/* Password */}
                <div>
                  <label style={{
                    display:'block', fontSize:10, fontWeight:700, color:T.muted,
                    letterSpacing:'.9px', textTransform:'uppercase', marginBottom:6,
                  }}>Password</label>
                  <div style={{ position:'relative' }}>
                    <input
                      className="login-input"
                      type={showPwd ? 'text' : 'password'}
                      placeholder="••••••••••"
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      autoComplete="current-password"
                      style={{ paddingRight:42, color:T.navy }}
                      required
                    />
                    <button
                      type="button"
                      className="pwd-toggle"
                      onClick={() => setShowPwd(v => !v)}
                      tabIndex={-1}
                      title={showPwd ? 'Hide password' : 'Show password'}
                      style={{ color:T.softGrey }}
                    >
                      {showPwd ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                {/* Inline error */}
                {error && (
                  <div style={{
                    background:T.dangerPale, border:`1.5px solid ${T.danger}28`,
                    borderRadius:9, padding:'10px 13px',
                  }}>
                    <p style={{ fontSize:13, color:T.danger, margin:0, fontWeight:500 }}>
                      ⚠️ {error}
                    </p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading || !tenantSlug}
                  style={{
                    marginTop:2,
                    background: loading || !tenantSlug
                      ? T.border
                      : `linear-gradient(135deg, ${T.teal} 0%, ${T.tealLight} 100%)`,
                    color: loading || !tenantSlug ? T.muted : T.white,
                    boxShadow: loading || !tenantSlug ? 'none' : `0 5px 18px ${T.teal}45`,
                  }}
                >
                  {loading
                    ? <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                        <span style={{
                          display:'inline-block', width:13, height:13, borderRadius:'50%',
                          border:`2px solid rgba(255,255,255,.35)`, borderTopColor:T.white,
                          animation:'spin 1s linear infinite',
                        }} />
                        Signing in…
                      </span>
                    : '→  Sign In'
                  }
                </button>
              </form>

              {/* Forgot password */}
              <div style={{
                textAlign:'center', marginTop:16, paddingTop:14,
                borderTop:`1px solid ${T.border}`,
              }}>
                <p style={{ fontSize:12, color:T.muted, margin:0 }}>
                  Forgot your password?{' '}
                  <span style={{ color:T.slate, fontWeight:500 }}>
                    Contact your property administrator.
                  </span>
                </p>
              </div>

            </div>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <footer
          className="page-footer"
          style={{ background:T.navy }}
        >
          <span style={{ color:T.gold, fontSize:12, lineHeight:1 }}>✦</span>
          <p style={{
            fontSize:11, fontWeight:500,
            color:'rgba(255,255,255,.65)',
            margin:0, letterSpacing:'.2px',
          }}>
            Powered by{' '}
            <strong style={{ color:T.tealLight, fontWeight:700 }}>
              Orinest Integrated Hospitality MIS
            </strong>
            {' '}·{' '}
            <span style={{ color:'rgba(255,255,255,.40)' }}>
              © {new Date().getFullYear()} All rights reserved
            </span>
          </p>
          <span style={{ color:T.gold, fontSize:12, lineHeight:1 }}>✦</span>
        </footer>

      </div>
    </>
  );
}